// src/components/booking/PublicBookingFlow.jsx
import React, { useState, useEffect } from 'react';
import { FiArrowLeft, FiCalendar, FiClock, FiUser, FiCheck, FiAlertCircle, FiCheckCircle, FiMail } from 'react-icons/fi';
import { collection, query, where, getDocs, getFirestore } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { tenantDataService } from '../../services/tenantDataService';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import { getAvailableSlots, submitBookingRequest } from '../../services/bookingApi';
import BookingHeader from './BookingHeader';

const PublicBookingFlow = () => {
  const { tenantSlug } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [error, setError] = useState(null);
  const [tenantData, setTenantData] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [clinicians, setClinicians] = useState([]);
  const [services, setServices] = useState([]);
  const [clientDetails, setClientDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    notes: ''
  });
  const [gdprConsent, setGdprConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Set default date to tomorrow
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  // Load tenant data
  useEffect(() => {
    const loadTenantData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const db = getFirestore();
        
        // Find tenant by slug
        const tenantsRef = collection(db, 'tenants');
        const q = query(tenantsRef, where('slug', '==', tenantSlug));
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
          throw new Error('Clinic not found');
        }
        
        const tenantDoc = snapshot.docs[0];
        const tenantData = { id: tenantDoc.id, ...tenantDoc.data() };
        
        // Check if booking is enabled (default to true if bookingSettings don't exist)
        const hasBookingSettings = tenantData.bookingSettings && typeof tenantData.bookingSettings === 'object';
        const bookingEnabled = hasBookingSettings 
          ? tenantData.bookingSettings.enablePublicBooking !== false 
          : true; // Default to enabled if no booking settings exist
        
        if (!bookingEnabled) {
          throw new Error('Online booking is not available for this clinic');
        }
        
        setTenantData(tenantData);
        
        // Load clinicians and services
        await Promise.all([
          loadClinicians(tenantData.id),
          loadServices(tenantData.id)
        ]);
        
      } catch (error) {
        console.error('Error loading tenant data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (tenantSlug) {
      loadTenantData();
    }
  }, [tenantSlug]);

  // Load clinicians
  const loadClinicians = async (tenantId) => {
    try {
      const db = getFirestore();
      const staffRef = collection(db, 'tenants', tenantId, 'staff');
      const q = query(staffRef, where('role', 'in', ['admin', 'clinician']));
      const snapshot = await getDocs(q);
      
      const cliniciansData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setClinicians(cliniciansData);
    } catch (error) {
      console.error('Error loading clinicians:', error);
    }
  };
  
  // Load services
  const loadServices = async (tenantId) => {
    try {
      tenantDataService.setTenantContext(tenantId);
      const settingsDoc = await tenantDataService.getById('settings', 'clinic_settings');
      
      if (settingsDoc?.treatmentRates) {
        // Filter services that have online booking enabled
        const servicesList = Object.entries(settingsDoc.treatmentRates)
          .filter(([name, rate]) => rate.onlineBooking !== false) // Default to true if not set
          .map(([name, rate]) => ({
            id: name.toLowerCase().replace(/\s+/g, '-'),
            name,
            price: rate.value,
            type: rate.type,
            duration: rate.type === 'hourly' ? 60 : 30 // Default durations
          }));
        
        setServices(servicesList);
        
        // Auto-select Physiotherapy if available
        const physiotherapyService = servicesList.find(service => 
          service.name.toLowerCase().includes('physiotherapy')
        );
        if (physiotherapyService && !selectedService) {
          setSelectedService(physiotherapyService);
        }
      }
    } catch (error) {
      console.error('Error loading services:', error);
      setServices([]);
    }
  };

  // Fetch available slots
  const fetchSlots = async () => {
    if (!selectedDate || !selectedClinician || !selectedService || !tenantData) return;
    
    try {
      setLoadingSlots(true);
      setError(null);
      
      const response = await getAvailableSlots({
        tenantId: tenantData.id,
        dateIso: selectedDate,
        clinicianId: selectedClinician.id,
        serviceType: selectedService.name
      });
      
      // Extract slots array from response object
      setAvailableSlots(response.slots || []);
      
    } catch (error) {
      console.error('Error fetching slots:', error);
      setError(error.message || 'Failed to load available times');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Submit booking request
  const handleSubmit = async () => {
    if (!selectedSlot || !tenantData || !selectedClinician || !selectedService) return;
    
    try {
      setSubmitting(true);
      setError(null);
      
      await submitBookingRequest({
        tenantId: tenantData.id,
        clinicianId: selectedClinician.id,
        dateIso: selectedDate,
        startLocal: selectedSlot.startLocal,
        patient: {
          firstName: clientDetails.firstName,
          lastName: clientDetails.lastName,
          email: clientDetails.email,
          phone: clientDetails.phone,
          dob: clientDetails.dateOfBirth
        },
        notes: clientDetails.notes
      });
      
      setStep(6); // Success step
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      setError(error.message || 'Failed to submit booking request');
    } finally {
      setSubmitting(false);
    }
  };

  // Navigation functions
  const nextStep = () => {
    if (step === 3 && selectedDate) {
      fetchSlots();
    }
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <BookingHeader tenantData={null} />
        <div className="flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading clinic information...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Booking Unavailable</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BookingHeader tenantData={tenantData} />
      
      {/* Progress indicator */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4, 5].map((stepNum) => (
              <React.Fragment key={stepNum}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  stepNum <= step 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {stepNum < step ? <FiCheckCircle className="w-5 h-5" /> : stepNum}
                </div>
                {stepNum < 5 && (
                  <div className={`flex-1 h-1 mx-2 ${
                    stepNum < step ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>Clinician</span>
            <span>Service</span>
            <span>Date</span>
            <span>Time</span>
            <span>Details</span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          
          {/* Step 1: Select Clinician */}
          {step === 1 && (
            <div>
              <h2 className="text-xl font-semibold mb-6 flex items-center">
                <FiUser className="w-5 h-5 mr-2" />
                Select a Clinician
              </h2>
              
              <div className="grid gap-4 md:grid-cols-2">
                {clinicians.map((clinician) => (
                  <div
                    key={clinician.id}
                    onClick={() => setSelectedClinician(clinician)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedClinician?.id === clinician.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <h3 className="font-medium text-gray-900">
                      {clinician.firstName} {clinician.lastName}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {clinician.role}
                    </p>
                    {clinician.specialties && (
                      <p className="text-sm text-gray-500 mt-2">
                        {clinician.specialties.join(', ')}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={nextStep}
                  disabled={!selectedClinician}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Select Service */}
          {step === 2 && (
            <div>
              <h2 className="text-xl font-semibold mb-6">
                Select a Service
              </h2>
              
              <div className="space-y-3">
                {services.map((service) => (
                  <div
                    key={service.name}
                    onClick={() => setSelectedService(service)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedService?.name === service.name
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {service.name}
                        </h3>
                        {service.description && (
                          <p className="text-sm text-gray-600 mt-1">
                            {service.description}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {service.duration || 60} mins
                        </p>
                        {service.price && (
                          <p className="text-sm text-gray-600">
                            £{service.price}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between mt-6">
                <button
                  onClick={prevStep}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400"
                >
                  Back
                </button>
                <button
                  onClick={nextStep}
                  disabled={!selectedService}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Select Date */}
          {step === 3 && (
            <div>
              <h2 className="text-xl font-semibold mb-6 flex items-center">
                <FiCalendar className="w-5 h-5 mr-2" />
                Select a Date
              </h2>
              
              {/* 24-hour booking policy notice */}
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start">
                  <FiAlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-blue-800 mb-1">
                      Advance Booking Required
                    </h3>
                    <p className="text-sm text-blue-700">
                      For appointments within the next 24 hours, please contact the clinic directly. 
                      You can book appointments from tomorrow onwards up to 4 weeks in advance.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="max-w-md">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  max={new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex justify-between mt-6">
                <button
                  onClick={prevStep}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400"
                >
                  Back
                </button>
                <button
                  onClick={nextStep}
                  disabled={!selectedDate}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Select Time Slot */}
          {step === 4 && (
            <div>
              <h2 className="text-xl font-semibold mb-6 flex items-center">
                <FiClock className="w-5 h-5 mr-2" />
                Select a Time
              </h2>
              
              {loadingSlots ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading available times...</p>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600">No available times for this date.</p>
                  <button
                    onClick={prevStep}
                    className="mt-4 text-blue-600 hover:text-blue-700"
                  >
                    Choose a different date
                  </button>
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-4">
                  {availableSlots.map((slot, index) => {
                    const startTime = new Date(slot.startLocal).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                    
                    return (
                      <button
                        key={index}
                        onClick={() => setSelectedSlot(slot)}
                        className={`p-3 border rounded-md text-center transition-colors ${
                          selectedSlot === slot
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {startTime}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-between mt-6">
                <button
                  onClick={prevStep}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400"
                >
                  Back
                </button>
                <button
                  onClick={nextStep}
                  disabled={!selectedSlot}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Client Details */}
          {step === 5 && (
            <div>
              <h2 className="text-xl font-semibold mb-6 flex items-center">
                <FiMail className="w-5 h-5 mr-2" />
                Your Details
              </h2>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={clientDetails.firstName}
                    onChange={(e) => setClientDetails({...clientDetails, firstName: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={clientDetails.lastName}
                    onChange={(e) => setClientDetails({...clientDetails, lastName: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={clientDetails.email}
                    onChange={(e) => setClientDetails({...clientDetails, email: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={clientDetails.phone}
                    onChange={(e) => setClientDetails({...clientDetails, phone: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={clientDetails.dateOfBirth}
                    onChange={(e) => setClientDetails({...clientDetails, dateOfBirth: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Notes
                </label>
                <textarea
                  value={clientDetails.notes}
                  onChange={(e) => setClientDetails({...clientDetails, notes: e.target.value})}
                  rows={3}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Any additional information or special requirements..."
                />
              </div>

              {/* GDPR Consent */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="gdpr-consent"
                    checked={gdprConsent}
                    onChange={(e) => setGdprConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="gdpr-consent" className="ml-3 text-sm text-gray-700">
                    I consent to the processing of my personal data for the purpose of booking and managing my appointment. 
                    I understand my rights under GDPR/PDPA and can withdraw consent at any time. *
                  </label>
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <button
                  onClick={prevStep}
                  className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={!clientDetails.firstName || !clientDetails.lastName || !clientDetails.email || !clientDetails.phone || !gdprConsent || submitting}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </div>
          )}

          {/* Step 6: Confirmation */}
          {step === 6 && (
            <div className="text-center">
              <FiCheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                Request Submitted Successfully!
              </h2>
              <p className="text-gray-600 mb-6">
                Thank you for your booking request. We'll review your request and get back to you within 24 hours.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-6 text-left max-w-md mx-auto">
                <h3 className="font-medium text-gray-900 mb-3">Booking Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Clinician:</span>
                    <span>{selectedClinician?.firstName} {selectedClinician?.lastName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service:</span>
                    <span>{selectedService?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span>{new Date(selectedDate).toLocaleDateString('en-GB')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time:</span>
                    <span>{selectedSlot && new Date(selectedSlot.startLocal).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => navigate('/')}
                className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
              >
                Return Home
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicBookingFlow;
